import type { AvailableExchanges, Timeframe, CandleConditionType, UserTriggerType } from '@bit-box/common';
import {
  AlertFrequencyType,
  ConditionOperatorType,
  IndicatorOrCondition,
  IndicatorSeriesType,
  CandleValueType,
  AutoMode,
  MarginMode,
  NotifierType,
  OrderType,
  InvestMode,
  AverageUnitType,
  CandleComputedType,
  OrderMode,
  SearchInvestMode,
  PositionType,
  EditMode,
  CutType,
  UpDown,
  PositionOrderType,
  TriggerTypeBase,
  TriggerType,
  ForcedExitType,
  OperandSourceType,
  ShapeType,
} from '@bit-box/common';
import type { MainTabType } from '../../common/enums';
import {
  SignalGridColIds,
  MainTabs,
  OrderGridColIds,
  PositionGridColIds,
  SearchOperatorType,
  MarketTabs,
  SymbolGridColIds,
  StrategyButtons,
  StrategyTabs,
  DropAction,
  TriggerTarget,
} from '../../common/enums';
import type { LocaleResources } from '../types';

export const ExchangeIdCaption: Partial<Record<(typeof AvailableExchanges)[number], string>> = {
  binanceusdm: 'Binance Future USDT Perpetual',
};

export const TimeframeCaption: Record<Timeframe, string> = {
  '1m': '1m',
  '3m': '3m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '2h',
  '4h': '4h',
  '6h': '6h',
  '8h': '8h',
  '12h': '12h',
  '1d': '1D',
  '3d': '3D',
  '1w': '1W',
  '1M': '1M',
};

export const CandleValueTypeCaption: Record<CandleValueType, string> = {
  [CandleValueType.OPEN]: 'Open',
  [CandleValueType.HIGH]: 'High',
  [CandleValueType.LOW]: 'Low',
  [CandleValueType.CLOSE]: 'Last',
  [CandleValueType.VOLUME]: 'Volume',
  [CandleValueType.TAKER_BUY_VOLUME]: 'Taker Buy Volume',
};

export const CandleComputedTypeCaption: Record<CandleComputedType, string> = {
  [CandleComputedType.CLOSE_OPEN_DELTA]: 'Last-Open Delta',
  [CandleComputedType.CLOSE_OPEN_DELTA_ABSOLUTE]: 'Last-Open Delta (Absolute)',
  [CandleComputedType.CLOSE_OPEN_DELTA_PERCENT]: 'Last-Open Delta (%)',
  [CandleComputedType.CLOSE_OPEN_DELTA_PERCENT_ABSOLUTE]: 'Last-Open Delta (% Absolute)',
  [CandleComputedType.HIGH_LOW_DELTA]: 'High-Low Delta',
  [CandleComputedType.HIGH_LOW_DELTA_PERCENT]: 'High-Low Delta (%)',
  [CandleComputedType.CLOSE_LOW_DELTA]: 'Last-Low Delta',
  [CandleComputedType.CLOSE_LOW_DELTA_PERCENT]: 'Last-Low Delta (%)',
  [CandleComputedType.CLOSE_HIGH_DELTA]: 'Last-High Delta',
  [CandleComputedType.CLOSE_HIGH_DELTA_PERCENT]: 'Last-High Delta (%)',
  [CandleComputedType.HL]: '(H+L)/2',
  [CandleComputedType.HLC]: '(H+L+C)/3',
  [CandleComputedType.OHLC]: '(O+H+L+C)/4',
  [CandleComputedType.HLCC]: '(H+L+C+C)/4',
};

export const CandleConditionTypeCaption: Record<CandleConditionType, string> = {
  ...CandleValueTypeCaption,
  ...CandleComputedTypeCaption,
};

export const ConditionOperatorTypeCaption: Record<ConditionOperatorType, string> = {
  [ConditionOperatorType.EQUAL]: 'Equal',
  [ConditionOperatorType.NOT_EQUAL]: 'Not Equal',
  [ConditionOperatorType.CROSSING]: 'Crossing',
  [ConditionOperatorType.CROSSING_UP]: 'Crossing Up',
  [ConditionOperatorType.CROSSING_DOWN]: 'Crossing Down',
  [ConditionOperatorType.GREATER_THAN]: 'Greater Than',
  [ConditionOperatorType.EQUAL_OR_GREATER_THAN]: 'Greater or Equal',
  [ConditionOperatorType.LESS_THAN]: 'Less Than',
  [ConditionOperatorType.EQUAL_OR_LESS_THAN]: 'Less or Equal',
  [ConditionOperatorType.CLOSER]: 'Getting Closer',
  [ConditionOperatorType.EQUAL_OR_CLOSER]: 'Equal or Closer',
  [ConditionOperatorType.FARTHER]: 'Getting Farther',
  [ConditionOperatorType.EQUAL_OR_FARTHER]: 'Equal or Farther',
  [ConditionOperatorType.ENTERING_CHANNEL]: 'Entering Channel',
  [ConditionOperatorType.EXITING_CHANNEL]: 'Exiting Channel',
  [ConditionOperatorType.INSIDE_CHANNEL]: 'Inside Channel',
  [ConditionOperatorType.OUTSIDE_CHANNEL]: 'Outside Channel',
  [ConditionOperatorType.WEBHOOK]: 'Webhook',
};

export const OperandSourceTypeCaption: Record<OperandSourceType, string> = {
  [OperandSourceType.FORMULA]: 'Formula',
  [OperandSourceType.OHLCV]: 'Candle',
  [OperandSourceType.VALUE]: 'Value',
  [OperandSourceType.INDICATOR]: 'Indicator',
  [OperandSourceType.SHAPE]: 'Shape',
  [OperandSourceType.WEBHOOK]: 'Webhook',
};

export const AlertFrequencyTypeCaption: Record<AlertFrequencyType, string> = {
  [AlertFrequencyType.REALTIME]: 'Real-time',
  [AlertFrequencyType.ONLY_ONCE]: 'Once Only',
  [AlertFrequencyType.ONLY_PER_BAR]: 'Once Per Bar',
  [AlertFrequencyType.ONLY_PER_BAR_CLOSE]: 'Once Per Bar Close',
  [AlertFrequencyType.ONLY_PER_MINUTE]: 'Once Per Minute',
};

export const IndicatorSeriesTypeCaption: Record<IndicatorSeriesType, string> = {
  [IndicatorSeriesType.AREA]: 'Area',
  [IndicatorSeriesType.ALTAREA]: 'Alt Area',
  [IndicatorSeriesType.LINE]: 'Line',
  [IndicatorSeriesType.BAR]: 'Bar',
  [IndicatorSeriesType.ALTBAR]: 'Alt Bar',
  [IndicatorSeriesType.CANDLE]: 'Candle',
  [IndicatorSeriesType.ALTLINE]: 'Alt Line',
};

export const MarginModeCaption: Record<MarginMode, string> = {
  [MarginMode.CROSS]: 'Cross',
  [MarginMode.ISOLATED]: 'Isolated',
};

/**
 * Interface Captions
 **/

export const AlertOperandCaption = {
  type: 'Type',
  sourceId: 'Source ID',
  output: 'Output',
  value: 'Value',
  valueOffset: 'Value Offset',
  valueOffsetByPercent: 'Percent',
  candleOffset: 'Candle Offset',
  strokeStyle: 'Line Color',
};

/**
 * todo: To be removed
 */
export const AlertConfigCaption = {
  title: 'Alert Condition',
  id: 'ID',
  name: 'Name',
  type: 'Type',
  timeframes: 'Timeframe Selection',
  operator: 'Condition',
  condition: 'Condition',
  operands: ['Source', 'Compare Value', 'Compare Value'],
  consecutive: 'Consecutive Match',
  useConsecutive: 'Consecutive Match',
  consecutiveCheckTooltip: 'Only consider matched when this condition matches consecutively within a specific range.',
  consecutiveInputTooltip:
    'Alert triggers only when consecutive matches are between min and max. Empty or 0 values are ignored.',
  minConsecutive: 'Min',
  maxConsecutive: 'Max',
  candleOffsetTooltip: `[Candle Offset] negative values reference past candles (-1 is previous candle), positive values reference future candles (1 is next candle).`,
  useOffsetCheckTooltip: 'Modify the candle position or transform the value of source or compare value for comparison.',
  useOffsetInputTooltip: `[Value Offset] adjusts the value. In [Percent] mode, applies the percentage to the source value.\n
  In [+/-] mode, adds or subtracts the entered value from the source value.`,
  frequency: 'Frequency',
  fillStyle: 'Channel Fill Color',
  expireAt: 'Expiry Time',
  disabled: 'Disabled',
  newPanel: 'New Panel',
};

export const ConditionFormCaption = {
  title: 'Condition',
  id: 'ID',
  name: 'Name',
  group: 'Group',
  timeframe: 'Time Candle',
  operator: 'Condition',
  operands: ['Source Value', 'Compare Value', 'Compare Value'],
  consecutive: 'Consecutive Match',
  useConsecutive: 'Consecutive Match',
  consecutiveCheckTooltip: 'Only consider matched when this condition matches consecutively within a specific range.',
  consecutiveInputTooltip:
    'Alert triggers only when consecutive matches are between min and max. Empty or 0 values are ignored.',
  minConsecutive: 'Min',
  maxConsecutive: 'Max',
  useOffsetCheckTooltip: 'Modify the candle position or transform the value of source or compare value for comparison.',
  useOffsetInputTooltip: `[Candle Offset] negative values reference past candles (-1 is previous candle), positive values reference future candles (1 is next candle).\n
  [Value Offset] specifies a value to add or subtract.\n
  With [Percent] checked, [Value Offset] is treated as a percentage to add or subtract from the current value.`,
  usages: 'Usage',
  signalName: 'Signal Name',
  disabled: 'Disable this condition',
  defaultGroup: 'Default Group',
  groupTooltip:
    'Conditions in the same group are compared with AND logic, while different groups are compared with OR logic.',
};

export const IndicatorInputCaption = {
  inReal: 'Source',
};

export const IndicatorOutputCaption = {
  value: 'Value',
  strokeStyle: 'Line Color',
  strokeDasharray: 'Line Style',
  strokeWidth: 'Line Width',
  hidden: 'Hidden',
};

export const IndicatorSeriesCaption = {
  seriesLine: 'Line',
  seriesFill: 'Fill',
  seriesShow: 'Show',
  seriesUp: 'Up',
  seriesDown: 'Down',
  seriesPlus: 'Plus',
  seriesMinus: 'Minus',
  seriesLine1: 'Line 1',
  seriesLine2: 'Line 2',
};

export const IndicatorConfigCaption = {
  title: 'Indicator',
  id: 'ID',
  funcId: 'Function ID',
  name: 'Name',
  inputs: 'Inputs',
  outputs: 'Outputs',
  series: 'Series',
  newPanel: 'New Panel',
  disabled: 'Disabled',
};

export const NotifierTypeCaption: Record<NotifierType, string> = {
  [NotifierType.TELEGRAM]: 'Telegram',
  [NotifierType.DISCORD]: 'Discord',
};

export const StrategyConfigCaption = {
  name: 'Name',
  timeframes: 'Indicator Generation Timeframes',
};

export const TimeframesConfigCaption = {
  title: 'Indicator Generation Timeframes',
};

export const ConfigTabkey = {
  exchange: 'exchange-config',
  invest: 'invest-config',
  notifier: 'notifier-config',
} as const;

export const ConfigTabCaption = {
  [ConfigTabkey.exchange]: 'Exchange',
  [ConfigTabkey.invest]: 'Calculation',
  [ConfigTabkey.notifier]: 'Notification',
};

export const InvestModeCaption: Record<InvestMode, string> = {
  [InvestMode.REAL]: 'Real',
  [InvestMode.SIMULATION]: 'Simulation',
  [InvestMode.BACKTEST]: 'Backtest',
};

export const SearchInvestModeCaption: Record<SearchInvestMode, string> = {
  [SearchInvestMode.ALL]: 'All',
  [SearchInvestMode.REAL]: InvestModeCaption[InvestMode.REAL],
  [SearchInvestMode.SIMULATION]: InvestModeCaption[InvestMode.SIMULATION],
  [SearchInvestMode.BACKTEST]: InvestModeCaption[InvestMode.BACKTEST],
  [SearchInvestMode.NONE]: 'Not Created',
};

export const OrderModeCaption: Record<OrderMode, string> = {
  [OrderMode.REAL]: InvestModeCaption[InvestMode.REAL],
  [OrderMode.SIMULATION]: InvestModeCaption[InvestMode.SIMULATION],
  [OrderMode.BACKTEST]: InvestModeCaption[InvestMode.BACKTEST],
  [OrderMode.BACK_SIGNAL]: 'Back Signal',
};

export const MainTabCaption: Record<MainTabType, string> = {
  [InvestMode.REAL]: InvestModeCaption[InvestMode.REAL],
  [InvestMode.SIMULATION]: InvestModeCaption[InvestMode.SIMULATION],
  [InvestMode.BACKTEST]: InvestModeCaption[InvestMode.BACKTEST],
  [MainTabs.CREATOR]: 'Strategy Creator',
  [MainTabs.STRATEGY]: 'Strategy',
};

export const StrategyTabCaption: Record<StrategyTabs, string> = {
  [StrategyTabs.TRIGGER]: 'Trade',
  [StrategyTabs.SIGNAL]: 'Signal',
  [StrategyTabs.INDICATOR]: 'Indicator',
};

export const StrategyButtonCaption: Partial<Record<StrategyButtons, string>> = {
  [StrategyButtons.SIGNAL_ENTER]: 'Trade',
  [StrategyButtons.SIGNAL]: StrategyTabCaption[StrategyTabs.SIGNAL],
  [StrategyButtons.INDICATOR]: StrategyTabCaption[StrategyTabs.INDICATOR],
};

export const CheckListButtonCaption = {
  checkList: 'Checklist',
};

export const ShapeTypeCaption: Record<ShapeType, string> = {
  [ShapeType.MEASURE]: 'Measure',

  // Lines
  [ShapeType.TREND_LINE]: 'Trend Line',
  [ShapeType.RAY]: 'Ray',
  [ShapeType.INFO_LINE]: 'Info Line',
  [ShapeType.HORZ_LINE]: 'Horizontal Line',
  [ShapeType.VERT_LINE]: 'Vertical Line',
  [ShapeType.PARALLEL_CHANNEL]: 'Parallel Channel',

  // Fibonacci
  [ShapeType.FIB_RETRACEMENT]: 'Fibonacci Retracement',
  [ShapeType.FIB_CHANNEL]: 'Fibonacci Channel',

  // Gann
  [ShapeType.GANN_BOX]: 'Gann Box',
  [ShapeType.GANN_FAN]: 'Gann Fan',

  // Patterns
  [ShapeType.ELLIOTT_IMPULSE_12345]: 'Elliott Impulse 12345',
  [ShapeType.ELLIOTT_CORRECTION_ABC]: 'Elliott Correction ABC',
  [ShapeType.XABCD_PATTERN]: 'XABCD Pattern',

  // Shapes
  [ShapeType.RECTANGLE]: 'Rectangle',
  [ShapeType.TRIANGLE]: 'Triangle',
  [ShapeType.PATH]: 'Path',

  // Text
  [ShapeType.TEXT]: 'Text',

  //volume profile
  [ShapeType.FIXED_RANGE_VOLUME_PROFILE]: 'Fixed Range Volume Profile',
  [ShapeType.ANCHORED_VOLUME_PROFILE]: 'Anchored Volume Profile',

  // Order
  [ShapeType.ORDER_LINE]: 'Order Line',
};

/**
 * Shape output captions
 * Note: Fibonacci levels are user-configurable per shape, not included here
 * Level values should be dynamically fetched from appearance.*Levels
 */
export const ShapeOutputCaption = {
  line: 'Line',
  upper: 'Upper',
  lower: 'Lower',
};

export const MarketTabCaption: Record<MarketTabs, string> = {
  [MarketTabs.POSITION]: 'Position',
  // [MarketTabs.ENTRY]: 'Entry Signal',
  [MarketTabs.INVEST]: 'Investment Candidate',
  [MarketTabs.SYMBOL]: 'Symbol Selection',
};

export const IndicatorOrConditionCaption: Record<IndicatorOrCondition, string> = {
  [IndicatorOrCondition.INDICATOR]: 'Indicator',
  [IndicatorOrCondition.CONDITION]: 'Condition',
};

export const SymbolGridColCaptions: Record<SymbolGridColIds, string> = {
  [SymbolGridColIds.SELECTED]: 'Invest',
  [SymbolGridColIds.SHORT_SYMBOL]: 'Symbol',
  [SymbolGridColIds.LEVERAGE]: 'Leverage',
  [SymbolGridColIds.PRICE]: 'Price',
  [SymbolGridColIds.QUOTE_VOLUME]: 'Volume',
  [SymbolGridColIds.PERCENTAGE]: 'Change %',
  [SymbolGridColIds.FROM_HIGH]: 'From High',
  [SymbolGridColIds.FROM_LOW]: 'From Low',
  [SymbolGridColIds.HIGH_LOW_CHANGE]: 'Range',
  [SymbolGridColIds.CORRELATION]: 'Correlation',
};

export const SignalGridColCaptions: Record<SignalGridColIds, string> = {
  [SignalGridColIds.MATCHED_AT]: 'Signal Time',
  [SignalGridColIds.ELAPSED_TIME]: 'Signal Age',
  [SignalGridColIds.SHORT_SYMBOL]: 'Symbol',
  [SignalGridColIds.PRICE]: 'Price',
  [SignalGridColIds.QUOTE_VOLUME]: 'Volume',
  [SignalGridColIds.PERCENTAGE]: 'Change %',
  [SignalGridColIds.FROM_HIGH]: 'From High',
  [SignalGridColIds.FROM_LOW]: 'From Low',
  [SignalGridColIds.HIGH_LOW_CHANGE]: 'Range',
  [SignalGridColIds.CHANGE]: 'Signal Change %',
  [SignalGridColIds.ENTER_POSITION]: 'Enter',
};

export const PositionGridColCaptions: Record<PositionGridColIds, string> = {
  [PositionGridColIds.TIMESTAMP]: 'Entry Time',
  [PositionGridColIds.ELAPSED_TIME]: 'Entry Age',
  [PositionGridColIds.SHORT_SYMBOL]: 'Symbol',
  [PositionGridColIds.QUOTE_AMOUNT]: 'Value',
  [PositionGridColIds.ENTER_PRICE]: 'Entry Price',
  [PositionGridColIds.PRICE]: 'Price',
  [PositionGridColIds.NET_PNL]: 'Net P&L',
  [PositionGridColIds.NET_ROE]: 'Net ROE',
  [PositionGridColIds.EXIT_TRIGGER]: 'Exit Signal',
  [PositionGridColIds.TREND]: 'Trend',
  [PositionGridColIds.LIQUIDATION_PRICE]: 'Liq. Price',
  [PositionGridColIds.EXIT_POSITION]: 'Manual Exit',
};

export const OrderGridColCaptions: Record<OrderGridColIds, string> = {
  [OrderGridColIds.NO]: 'No',
  [OrderGridColIds.SYMBOL]: 'Symbol',
  [OrderGridColIds.QUOTE_AMOUNT]: 'Seed',
  [OrderGridColIds.DATETIME]: 'Time',
  [OrderGridColIds.POSITION_ORDER_TYPE]: 'Type',
  [OrderGridColIds.LEVERAGE]: 'Leverage',
  [OrderGridColIds.MARGIN_MODE]: 'Margin Mode',
  [OrderGridColIds.ENTER_COST]: 'Investment',
  [OrderGridColIds.PRICE]: 'Price',
  [OrderGridColIds.AMOUNT]: 'Amount',
  [OrderGridColIds.EXIT_COST]: 'Exit Value',
  [OrderGridColIds.PNL]: 'P&L',
  [OrderGridColIds.TRADE_FEE]: 'Fee',
  [OrderGridColIds.NET_PNL]: 'Net P&L',
};
export const UseStepsCaption = {
  false: 'Full',
  true: 'Split',
};

export const AverageUnitTypeCaption: Record<AverageUnitType, string> = {
  [AverageUnitType.SECOND]: 'sec',
  [AverageUnitType.MINUTE]: 'min',
};

export const SearchOperatorTypeCaption: Record<SearchOperatorType, string> = {
  [SearchOperatorType.NONE]: 'Condition',
  [SearchOperatorType.EQUAL_OR_GREATER_THAN]: '>=',
  [SearchOperatorType.EQUAL_OR_LESS_THAN]: '<=',
  [SearchOperatorType.GREATER_THAN]: '>',
  [SearchOperatorType.LESS_THAN]: '<',
  [SearchOperatorType.INCLUDE]: 'Contains',
  [SearchOperatorType.EQUAL]: 'Equals',
};

export const DropActionCaption: Record<DropAction, string> = {
  [DropAction.COPY]: 'Copy',
  [DropAction.MOVE]: 'Move',
  [DropAction.SHARE]: 'Share',
};

export const TriggerTargetCaption: Record<TriggerTarget, string> = {
  [TriggerTarget.SESSION]: 'Total',
  [TriggerTarget.POSITION]: 'Per Symbol',
};

export const EditModeCaption: Record<EditMode, string> = {
  [EditMode.ADD]: 'Add',
  [EditMode.EDIT]: 'Edit',
  [EditMode.DELETE]: 'Delete',
};

export const PositionTypeCaption: Record<PositionType, string> = {
  [PositionType.LONG]: 'Long',
  [PositionType.SHORT]: 'Short',
};

export const CutTypeCaption: Record<CutType, string> = {
  [CutType.TAKE_PROFIT]: 'Take Profit',
  [CutType.STOP_LOSS]: 'Stop Loss',
};

export const UpDownCaption: Record<UpDown, string> = {
  [UpDown.DOWN]: 'Down',
  [UpDown.UP]: 'Up',
};

export const OrderTypeCaption: Record<OrderType, string> = {
  [OrderType.ENTER]: 'Enter',
  [OrderType.EXIT]: 'Exit',
};

export const IsManualCaption = {
  true: 'Manual',
  false: 'Auto',
};

export const AutoModeCaption: Record<AutoMode, string> = {
  [AutoMode.BOTH]: `${IsManualCaption.false} ${OrderTypeCaption[OrderType.ENTER]} - ${IsManualCaption.false} ${OrderTypeCaption[OrderType.EXIT]}`,
  [AutoMode.ENTER]: `${IsManualCaption.false} ${OrderTypeCaption[OrderType.ENTER]} - ${IsManualCaption.true} ${OrderTypeCaption[OrderType.EXIT]}`,
  [AutoMode.EXIT]: `${IsManualCaption.true} ${OrderTypeCaption[OrderType.ENTER]} - ${IsManualCaption.false} ${OrderTypeCaption[OrderType.EXIT]}`,
  [AutoMode.MANUAL]: `${IsManualCaption.true} ${OrderTypeCaption[OrderType.ENTER]} - ${IsManualCaption.true} ${OrderTypeCaption[OrderType.EXIT]}`,
};

export const PositionOrderTypeCaption: Record<PositionOrderType, string> = {
  [PositionOrderType.LONG_ENTER]: `${PositionTypeCaption[PositionType.LONG]}${OrderTypeCaption[OrderType.ENTER]}`,
  [PositionOrderType.LONG_EXIT]: `${PositionTypeCaption[PositionType.LONG]}${OrderTypeCaption[OrderType.EXIT]}`,
  [PositionOrderType.SHORT_ENTER]: `${PositionTypeCaption[PositionType.SHORT]}${OrderTypeCaption[OrderType.ENTER]}`,
  [PositionOrderType.SHORT_EXIT]: `${PositionTypeCaption[PositionType.SHORT]}${OrderTypeCaption[OrderType.EXIT]}`,
};

export const TriggerTypeBaseCaption: Record<TriggerTypeBase, string> = {
  [TriggerTypeBase.SIGNAL]: 'Signal',
  [TriggerTypeBase.TRAILING]: 'Trailing',
  [TriggerTypeBase.EXCLUSIVE]: 'Exclusive',
  [TriggerTypeBase.OVERLAPPING]: 'Overlapping',
  [TriggerTypeBase.FORCED]: 'Forced',
};

export const UserTriggerTypeCaption: Record<UserTriggerType, string> = {
  [TriggerType.SIGNAL_ENTER]: `${TriggerTypeBaseCaption[TriggerTypeBase.SIGNAL]} ${OrderTypeCaption[OrderType.ENTER]}`,
  [TriggerType.SIGNAL_EXIT]: `${TriggerTypeBaseCaption[TriggerTypeBase.SIGNAL]} ${OrderTypeCaption[OrderType.EXIT]}`,
  [TriggerType.TRAILING_EXIT]: `${TriggerTypeBaseCaption[TriggerTypeBase.TRAILING]} ${OrderTypeCaption[OrderType.EXIT]}`,
  [TriggerType.EXCLUSIVE_EXIT]: `${TriggerTypeBaseCaption[TriggerTypeBase.EXCLUSIVE]} ${OrderTypeCaption[OrderType.EXIT]}`,
  [TriggerType.OVERLAPPING_EXIT]: `${TriggerTypeBaseCaption[TriggerTypeBase.OVERLAPPING]} ${OrderTypeCaption[OrderType.EXIT]}`,
};

export const TriggerTypeCaption: Record<TriggerType, string> = {
  ...UserTriggerTypeCaption,
  [TriggerType.FORCED_EXIT]: `Forced ${OrderTypeCaption[OrderType.EXIT]}`,
};

export const ForceExitTypeCaption: Record<ForcedExitType, string> = {
  [ForcedExitType.DURATION]: 'Duration',
  [ForcedExitType.PROFIT_GOAL]: 'Profit Goal',
  [ForcedExitType.LOSS_GOAL]: 'Loss Goal',
  [ForcedExitType.TERMINATE]: 'Program Terminate',
  [ForcedExitType.STOP]: 'Investment Stop',
  [ForcedExitType.SUSPEND]: 'Suspend Mode Enter',
  [ForcedExitType.EXPIRED]: 'Subscription Expired',
  [ForcedExitType.ERROR]: 'Error',
  [ForcedExitType.SESSION_TRIGGER]: 'Session Trigger',
  [ForcedExitType.LIQUIDATION]: 'Force Liquidation',
};

export const OVERLAPPING_STEP_EXIT = `${TriggerTypeCaption[TriggerType.OVERLAPPING_EXIT]} Stop`;
/**
 * Unified object containing all enums
 * Accessible via useLocaleResources()
 */
export const enums: LocaleResources['enums'] = {
  ExchangeIdCaption,
  TimeframeCaption,
  CandleValueTypeCaption,
  CandleComputedTypeCaption,
  CandleConditionTypeCaption,
  ConditionOperatorTypeCaption,
  OperandSourceTypeCaption,
  AlertFrequencyTypeCaption,
  IndicatorSeriesTypeCaption,
  AutoModeCaption,
  MarginModeCaption,
  AlertOperandCaption,
  AlertConfigCaption,
  ConditionFormCaption,
  IndicatorInputCaption,
  IndicatorOutputCaption,
  IndicatorConfigCaption,
  NotifierTypeCaption,
  StrategyConfigCaption,
  TimeframesConfigCaption,
  ConfigTabkey,
  ConfigTabCaption,
  InvestModeCaption,
  SearchInvestModeCaption,
  OrderModeCaption,
  MainTabCaption,
  StrategyTabCaption,
  StrategyButtonCaption,
  CheckListButtonCaption,
  ShapeTypeCaption,
  ShapeOutputCaption,
  MarketTabCaption,
  IndicatorOrConditionCaption,
  SymbolGridColCaptions,
  SignalGridColCaptions,
  PositionGridColCaptions,
  OrderGridColCaptions,
  UseStepsCaption,
  AverageUnitTypeCaption,
  SearchOperatorTypeCaption,
  DropActionCaption,
  TriggerTargetCaption,
  PositionTypeCaption,
  CutTypeCaption,
  UpDownCaption,
  OrderTypeCaption,
  IsManualCaption,
  PositionOrderTypeCaption,
  EditModeCaption,
  TriggerTypeBaseCaption,
  UserTriggerTypeCaption,
  TriggerTypeCaption,
  ForceExitTypeCaption,
  OVERLAPPING_STEP_EXIT,
} as const;
